import React, { useEffect, useState } from 'react';
import { Button, Form, Modal, Message } from 'semantic-ui-react';
import { OrganizationRecordValue, OrganizationRecordValueCreateUpdate, OrganizationRecordValueCreateUpdateTypeOfValueEnum } from 'store/generated/admin/organization-record-values';
import { organizationRecordValueApi } from '../api/organizationRecordValueApi';

interface Props {
    open: boolean;
    organizationId: number;
    onClose: () => void;
    onSuccess: () => void;
    fieldToEdit?: OrganizationRecordValue | null;
}

const OrganizationRecordModal: React.FC<Props> = ({ open, organizationId, onClose, onSuccess, fieldToEdit }) => {
    const [name, setName] = useState('');
    const [typeOfValue, setTypeOfValue] = useState(2);
    const [minRange, setMinRange] = useState(0);
    const [maxRange, setMaxRange] = useState(100);
    const [errors, setErrors] = useState<Record<string, string>>({});

    const [createFieldType, { isLoading: isCreating }] = organizationRecordValueApi.endpoints.apiAdminV1OrganizationRecordValuesCreate.useMutation();
    const [updateFieldType, { isLoading: isUpdating }] = organizationRecordValueApi.endpoints.apiAdminV1OrganizationRecordValuesUpdate.useMutation();

    useEffect(() => {
        if (fieldToEdit) {
            setName(fieldToEdit.name || '');
            const type = fieldToEdit.type_of_value || 2;
            setTypeOfValue(type);
            setMinRange(fieldToEdit.min_range ?? 0);
            setMaxRange(fieldToEdit.max_range ?? (type === 3 ? 10 : 100));
        } else {
            setName('');
            setTypeOfValue(2);
            setMinRange(0);
            setMaxRange(100);
        }
        setErrors({});
    }, [fieldToEdit, open]);

    const validate = (): boolean => {
        const newErrors: Record<string, string> = {};
        if (!name.trim()) newErrors.name = 'Field name is required';
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleTypeChange = (value: number) => {
        setTypeOfValue(value);
        // Update max_range default when type changes (only if not editing)
        if (!fieldToEdit) {
            setMaxRange(value === 3 ? 10 : 100);
        }
    };

    const handleSave = async () => {
        if (!validate()) return;
        const payload: OrganizationRecordValueCreateUpdate = {
            name,
            organization: organizationId,
            type_of_value: typeOfValue as OrganizationRecordValueCreateUpdateTypeOfValueEnum,
            min_range: minRange,
            max_range: maxRange,
        };

        try {
            if (fieldToEdit?.id) {
                await updateFieldType({ id: fieldToEdit.id, organizationRecordValueCreateUpdate: payload }).unwrap();
            } else {
                await createFieldType({ organizationRecordValueCreateUpdate: payload }).unwrap();
            }
            onSuccess();
            onClose();
        } catch (err) {
            console.error('Save failed:', err);
        }
    };

    const typeOfValueOptions = [
        // TP: This is an IL vestige. Number supports integers and decimals. No support for Date and Text currently.
        //{ key: 1, text: 'Text/Character', value: 1 },
        { key: 2, text: 'Number', value: 2 },
        //{ key: 3, text: 'Decimal', value: 3 },
        //{ key: 4, text: 'Date', value: 4 },
    ];

    return (
        <Modal open={open} onClose={onClose} size="small">
            <Modal.Header>{fieldToEdit ? 'Edit Organization Record' : 'Create Organization Record'}</Modal.Header>
            <Modal.Content>
                <Form>
                    <Form.Field error={!!errors.name} required>
                        <label>Field Name</label>
                        <Form.Input value={name} onChange={(e, { value }) => setName(value)} />
                        {errors.name && <Message negative size="tiny">{errors.name}</Message>}
                    </Form.Field>
                    <Form.Field>
                        <label>Type of Value</label>
                        <Form.Select
                            options={typeOfValueOptions}
                            value={typeOfValue}
                            onChange={(e, { value }) => handleTypeChange(value as number)}
                            fluid
                        />
                    </Form.Field>
                    <Form.Field>
                        <label>Minimum Range</label>
                        <Form.Input
                            type="number"
                            value={minRange}
                            onChange={(e, { value }) => setMinRange(Number(value))}
                        />
                    </Form.Field>
                    <Form.Field>
                        <label>Maximum Range</label>
                        <Form.Input
                            type="number"
                            value={maxRange}
                            onChange={(e, { value }) => setMaxRange(Number(value))}
                        />
                    </Form.Field>
                </Form>
            </Modal.Content>
            <Modal.Actions>
                <Button onClick={onClose} disabled={isCreating || isUpdating}>Cancel</Button>
                <Button
                    primary
                    onClick={handleSave}
                    loading={isCreating || isUpdating}
                    disabled={!name}
                >
                    Save
                </Button>
            </Modal.Actions>
        </Modal>
    );
};

export default OrganizationRecordModal;
